import type {Color} from "@cm/graph-v3"

/**
 * Transcribed from rgb_to_hsv / hsv_to_rgb / rgb_to_hsl / hsl_to_rgb in Blender's math_color.cc, rounding
 * to float32 at each step as C does. Two hues that sit exactly half the colour wheel apart land either
 * side of the boundary depending on that rounding, and the hue interpolation mode branches on it.
 */

const f = Math.fround
const clamp01 = (value: number) => Math.max(0, Math.min(1, value))

export const rgbToHsv = ({r, g, b}: Color): [number, number, number] => {
    let red = f(r)
    let green = f(g)
    let blue = f(b)
    let k = 0
    if (green < blue) {
        ;[green, blue] = [blue, green]
        k = -1
    }
    let minGreenBlue = blue
    if (red < green) {
        ;[red, green] = [green, red]
        k = f(f(-2 / 6) - k)
        minGreenBlue = Math.min(green, blue)
    }
    const chroma = f(red - minGreenBlue)
    const hue = Math.abs(f(k + f(f(green - blue) / f(f(6 * chroma) + f(1e-20)))))
    return [f(hue), f(chroma / f(red + f(1e-20))), red]
}

export const hsvToRgb = ([h, s, v]: [number, number, number]): Color => {
    const nr = clamp01(Math.abs(h * 6 - 3) - 1)
    const ng = clamp01(2 - Math.abs(h * 6 - 2))
    const nb = clamp01(2 - Math.abs(h * 6 - 4))
    return {r: ((nr - 1) * s + 1) * v, g: ((ng - 1) * s + 1) * v, b: ((nb - 1) * s + 1) * v}
}

export const rgbToHsl = ({r, g, b}: Color): [number, number, number] => {
    const red = f(r)
    const green = f(g)
    const blue = f(b)
    const cmax = Math.max(red, green, blue)
    const cmin = Math.min(red, green, blue)
    const l = Math.min(1, f(f(cmax + cmin) / 2))
    if (cmax === cmin) return [0, 0, l]
    const d = f(cmax - cmin)
    const s = l > 0.5 ? f(d / f(f(2 - cmax) - cmin)) : f(d / f(cmax + cmin))
    const h = cmax === red ? f(f(f(green - blue) / d) + (green < blue ? 6 : 0)) : cmax === green ? f(f(f(blue - red) / d) + 2) : f(f(f(red - green) / d) + 4)
    return [f(h / 6), s, l]
}

export const hslToRgb = ([h, s, l]: [number, number, number]): Color => {
    const nr = clamp01(Math.abs(h * 6 - 3) - 1)
    const ng = clamp01(2 - Math.abs(h * 6 - 2))
    const nb = clamp01(2 - Math.abs(h * 6 - 4))
    const chroma = (1 - Math.abs(2 * l - 1)) * s
    return {r: (nr - 0.5) * chroma + l, g: (ng - 0.5) * chroma + l, b: (nb - 0.5) * chroma + l}
}

/**
 * Transcribed from colorband_hue_interp in Blender's colorband.cc. The mode decides which way round the
 * hue circle the blend travels, so hueA and hueB must stay in Blender's order: the right stop, then the left.
 */
export const interpolateHue = (mode: string, weightA: number, weightB: number, hueA: number, hueB: number): number => {
    const wrap = (hue: number) => (hue < 1 ? hue : f(hue - 1))
    const h1 = wrap(hueA)
    const h2 = wrap(hueB)
    const interpolate = (a: number, b: number) => weightA * a + weightB * b

    let direction: 0 | 1 | 2 = 0
    const gap = f(h2 - h1)
    if (mode === "NEAR") {
        if (h1 < h2 && gap > 0.5) direction = 1
        else if (h1 > h2 && gap < -0.5) direction = 2
    } else if (mode === "FAR") {
        if (h1 === h2 || (h1 < h2 && gap < 0.5)) direction = 1
        else if (h1 > h2 && gap > -0.5) direction = 2
    } else if (mode === "CCW") {
        if (h1 > h2) direction = 2
    } else if (mode === "CW") {
        if (h1 < h2) direction = 1
    }

    if (direction === 1) return wrap(interpolate(h1 + 1, h2))
    if (direction === 2) return wrap(interpolate(h1, h2 + 1))
    return interpolate(h1, h2)
}
